import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  FieldsLayout,
  ModalShell,
  PageBackdrop,
  ReferralBlock,
  ThankYou,
  isReferralField,
  shade,
  submitLabel,
  usePublicFormFonts
} from './publicFormUi';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000'
});

function initialValues(fields, referralCode) {
  return (fields || []).reduce((values, field) => {
    if (isReferralField(field) && referralCode) {
      values[field.name] = referralCode;
    } else {
      values[field.name] = field.type === 'checkbox' && (field.options || []).length ? [] : '';
    }
    return values;
  }, {});
}

function setPageIdentity(title) {
  document.title = title || 'SI<3>';
  let icon = document.querySelector('link[rel="icon"]');
  if (!icon) {
    icon = document.createElement('link');
    icon.rel = 'icon';
    document.head.appendChild(icon);
  }
  icon.type = 'image/svg+xml';
  icon.href = `${process.env.PUBLIC_URL || ''}/favicon.svg`;
}

export default function PublicForm({ slug }) {
  usePublicFormFonts();
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [copied, setCopied] = useState(false);
  const [referral, setReferral] = useState({ code: '', valid: false, partnerName: '', message: '', applying: false });
  const startTracked = useRef(false);

  useEffect(() => {
    let active = true;
    setPageIdentity('SI<3>');

    async function load() {
      setLoading(true);
      setError('');
      setSuccess(false);
      setEmailSent(false);
      try {
        const formResponse = await API.get(`/api/forms/slug/${encodeURIComponent(slug)}`);
        if (!active) return;

        const loadedForm = formResponse.data?.data;
        setPageIdentity(loadedForm?.meta?.title || loadedForm?.title || 'SI<3>');
        const code = new URLSearchParams(window.location.search).get('ref')?.trim().toUpperCase() || '';
        let nextReferral = { code, valid: false, partnerName: '', message: '', applying: false };

        if (code) {
          try {
            const referralResponse = await API.get(`/api/referrals/validate/${encodeURIComponent(code)}`, {
              params: { formId: loadedForm._id }
            });
            nextReferral = {
              code,
              valid: true,
              partnerName: referralResponse.data?.data?.partnerName || referralResponse.data?.data?.label || '',
              message: '',
              applying: false
            };
          } catch (referralError) {
            nextReferral.message = referralError.response?.data?.error || 'This code is not active. Please double-check it.';
          }
        }

        if (!active) return;
        setForm(loadedForm);
        setReferral(nextReferral);
        setValues(initialValues(loadedForm.fields, nextReferral.valid ? code : ''));
      } catch (loadError) {
        if (active) setError(loadError.response?.data?.error || 'This form could not be loaded.');
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => { active = false; };
  }, [slug]);

  const publicUrl = form?.publicUrl || `${window.location.origin}/forms/${slug}`;
  const referralField = (form?.fields || []).find(isReferralField);
  const brand = form?.buttonColor || '#6B46FE';

  const leaveSite = () => {
    window.location.href = 'https://si3.space';
  };

  const trackStart = async () => {
    if (startTracked.current || !referral.valid || !form?._id) return;
    startTracked.current = true;
    try {
      await API.post('/api/referrals/track', {
        code: referral.code,
        event: 'start',
        formId: form._id
      });
    } catch {
      // Tracking must never prevent someone from completing the form.
    }
  };

  const setValue = (name, value) => {
    setValues(current => ({ ...current, [name]: value }));
    trackStart();
  };

  const applyReferral = async () => {
    const code = String(referral.code || values[referralField?.name] || '').trim().toUpperCase();
    if (code.length < 4 || !form?._id) return;
    setReferral(current => ({ ...current, applying: true, code, message: '' }));
    try {
      const referralResponse = await API.get(`/api/referrals/validate/${encodeURIComponent(code)}`, {
        params: { formId: form._id }
      });
      if (referralField) setValue(referralField.name, code);
      setReferral({
        code,
        valid: true,
        partnerName: referralResponse.data?.data?.partnerName || referralResponse.data?.data?.label || '',
        message: '',
        applying: false
      });
    } catch {
      setReferral({
        code,
        valid: false,
        partnerName: '',
        message: 'This code is not active. Please double-check it.',
        applying: false
      });
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const submitResponse = await API.post(`/api/forms/slug/${encodeURIComponent(slug)}/submit`, {
        data: values,
        referralCode: referral.valid ? referral.code : undefined
      });
      setEmailSent(submitResponse.data?.email?.ok === true);
      setSuccess(true);
    } catch (submitError) {
      setError(submitError.response?.data?.error || 'Your response could not be submitted. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      window.prompt('Copy form URL', publicUrl);
    }
  };

  if (loading) {
    return (
      <PageBackdrop>
        <ModalShell title="SI<3>" description="Loading form…">
          <div />
        </ModalShell>
      </PageBackdrop>
    );
  }

  if (!form) {
    return (
      <PageBackdrop>
        <ModalShell title="SI<3>" description={error || 'Form not found.'} onClose={leaveSite}>
          <div />
        </ModalShell>
      </PageBackdrop>
    );
  }

  return (
    <PageBackdrop>
      <ModalShell
        title={success ? '' : form.title}
        description={success ? '' : form.description}
        image={form.image}
        onClose={leaveSite}
      >
        {success ? (
          <ThankYou
            form={form}
            values={values}
            referral={referral}
            publicUrl={publicUrl}
            copied={copied}
            onCopy={copyUrl}
            onDone={leaveSite}
            emailSent={emailSent}
          />
        ) : (
          <form onSubmit={handleSubmit}>
            <FieldsLayout
              fields={form.fields}
              values={values}
              onChange={setValue}
              brandColor={brand}
              uploadUrl={`${API.defaults.baseURL || ''}/api/upload`}
            />

            {form.showReferral !== false ? (
            <div style={{ marginTop: 22 }}>
              <ReferralBlock
                referral={referral}
                onCodeChange={code => setReferral(current => ({ ...current, code, message: '' }))}
                onApply={applyReferral}
                onRemove={() => {
                  startTracked.current = false;
                  if (referralField) setValues(current => ({ ...current, [referralField.name]: '' }));
                  setReferral({ code: '', valid: false, partnerName: '', message: '', applying: false });
                }}
                brandColor={brand}
              />
            </div>
            ) : null}

            {error ? (
              <div role="alert" style={{ marginTop: 16, padding: '12px 14px', borderRadius: 12, background: 'rgba(127,29,29,.45)', color: '#FECACA', fontSize: 14 }}>
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={submitting}
              style={{
                width: '100%',
                minHeight: 52,
                marginTop: 22,
                border: 0,
                borderRadius: 12,
                background: `linear-gradient(90deg, ${brand}, ${shade(brand, -36)})`,
                color: '#fff',
                fontSize: 15,
                fontWeight: 700,
                opacity: submitting ? 0.65 : 1,
                cursor: submitting ? 'default' : 'pointer'
              }}
            >
              {submitting ? 'Submitting…' : submitLabel(form)}
            </button>
          </form>
        )}
      </ModalShell>
    </PageBackdrop>
  );
}
